import React, { useEffect, useState } from "react";
import { Link } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Mail,
  Phone,
  Star,
  X,
} from "lucide-react";
import ContactForm from "./ContactForm";
import LocationsMap from "./locations-map";
import siteConfig from "../siteConfig";
import { BBB_PROFILE, GAF_PROFILE, GOOGLE_REVIEWS, YELP_PROFILE } from "../reviewProfiles";
import "../styles/roach-homepage.css";
import "../styles/roach-about.css";

const asset = (name) => `/assets/roach-homepage/${name}`;

// Profile URLs supplied as Figma comments on the About frame (node 399:801):
//   #75 Yelp, #76 Google reviews, #77 BBB, #78 GAF.

// Hero collage. Same Contentful assets already running on the live About page;
// only `fm=webp` is added so the Images API serves WebP instead of JPEG/PNG.
const heroCollage = [
  [
    "https://images.ctfassets.net/dnje5q8u1u3a/3aB128GqwUudMaDo0iK5Sf/3a8ef608496ee113b653aab288d23df3/Resized_20231122_143852.jpeg?h=400&fm=webp&q=80",
  ],
  [
    "https://images.ctfassets.net/dnje5q8u1u3a/6awvekaCdWqMto4ohLfCgp/a95cdb9a51898ca92f1fc8c764a69ea5/sds.jpg?h=400&fm=webp&q=80",
    "https://images.ctfassets.net/dnje5q8u1u3a/S4Ioz4bTAOpVsMxbSeOsO/624ec36af2ab6af3bd63b60b2e4ac387/IMG_0231.jpeg?h=350&fm=webp&q=80",
  ],
  [
    "https://images.ctfassets.net/dnje5q8u1u3a/3xaXIMgJhiGDlV2NRNjQgl/12907ac1df3e49ca52d2b7cc2e318950/Screenshot_2024-02-25_at_4.02.05_PM.png?h=300&fm=webp&q=80",
    "https://images.ctfassets.net/dnje5q8u1u3a/7GpbutKRTiAytgA4621ekT/15e63af468b2fe36b4a9bfa22dc566e9/Screenshot_2024-02-25_at_11.18.39_PM.png?h=300&fm=webp&q=80",
  ],
];

const stats = [
  ["5000+", "Home improvement projects completed"],
  ["4.9/5", "Average rating from customers"],
  ["Michigan", "based contractors and specialists"],
];

const companyFacts = [
  ["Phone", "(269) 420-6623", "tel:+12694206623"],
  ["Availability", "Same-Day"],
  ["Service Area", "South-West Michigan"],
  ["License No", "2101200181"],
  ["Emergency Service", "24/7"],
  ["Years in business", "20+ years"],
];

const pillars = [
  {
    title: "The Mission That Connects Us",
    text: "To give West Michigan homeowners a roofing and remodeling experience built on straightforward pricing, quality materials, and work that actually lasts. As Shaun Roach put it when he started this company: homeowners deserve clear pricing and work that lasts, that's still the standard every job on our crews is held to today. From our three offices in Battle Creek, Wyoming, and Jackson, we bring GAF Master Elite and Owens Corning certified installation, CertainTeed materials, and a 25-year leak warranty to every roof, and we handle the insurance paperwork so storm damage doesn't become a second headache on top of the first.",
    image: asset("roofing-service.webp"),
    alt: "New roof overlooking a West Michigan neighborhood",
  },
  {
    title: "Our Vision",
    text: "To be the roofing and home improvement contractor West Michigan families call first, not because we're the cheapest bid, but because we're the one that shows up, does the job right the first time, and stands behind it after the trucks leave. Twenty years and 5,000+ roofs after Shaun started out of a home office and garage, that vision hasn't changed: build a company homeowners trust enough to refer to their neighbors, one roof at a time.",
    image: asset("project-01.webp"),
    alt: "Michigan home with a completed Roach Home Improvement roof",
    reversed: true,
  },
];

// `thumb` (640px) feeds the grid; the full-size `src` is only fetched when the
// lightbox opens.
const projects = [
  { src: asset("project-01.webp"), thumb: asset("project-01-640.webp"), alt: "Completed Roach Home Improvement project at a Michigan home" },
  { src: asset("project-02.webp"), thumb: asset("project-02-640.webp"), alt: "Roach Home Improvement office and wrapped company vehicle" },
  { src: asset("project-03.webp"), thumb: asset("project-03-640.webp"), alt: "Aerial view of a completed residential roof replacement" },
  { src: asset("project-04.webp"), thumb: asset("project-04-640.webp"), alt: "Roach roofing crew working on a home during winter" },
  { src: asset("project-05.webp"), thumb: asset("project-05-640.webp"), alt: "Michigan home with a completed Roach roof installation" },
  { src: asset("project-06.webp"), thumb: asset("project-06-640.webp"), alt: "Completed roofing and exterior project on a Michigan home" },
  { src: asset("project-07.webp"), thumb: asset("project-07-640.webp"), alt: "Rear exterior of a completed residential roofing project" },
  { src: asset("project-08.webp"), thumb: asset("project-08-640.webp"), alt: "Roach Home Improvement roadside billboard" },
  { src: asset("project-09.webp"), thumb: asset("project-09-640.webp"), alt: "Roach Home Improvement office and company trucks" },
  { src: asset("project-10.webp"), thumb: asset("project-10-640.webp"), alt: "Roach Home Improvement truck and project sign at a job site" },
];

const testimonials = [
  {
    title: "We suffered significant roof and gutter damage from a storm",
    body: "Roach Home Improvement worked with our insurance company and made the paperwork pretty painless. The crew came in and installed our new roof in a single day.",
    author: "Kevin Rabineau",
  },
  {
    title: "Definitely recommend Roach! Such a good experience",
    body: "They were super nice and they didn't mind getting more into detail because I needed a little more assurance after what I went through with another company.",
    author: "Nic Liceaga",
  },
  {
    title: "100% call backs, information asap, and they show up on time",
    body: "The best place I've ever dealt with! We had different crews come for our home work and every time they were polite, respectful and cleaned up like I'd want.",
    author: "Wesley Fry",
  },
];

const coreValues = [
  {
    title: "Honesty in Every Estimate",
    text: "No hidden costs, no surprise line items after the crew shows up. What we quote is what you pay, and if the scope changes once we're on the roof, we tell you before we touch it.",
  },
  {
    title: "Craftsmanship That Lasts",
    text: "We don't shingle over a bad problem, full tear-off, decking inspection, and manufacturer-spec installation on every job, backed by a 25-year leak warranty and our own workmanship guarantee.",
  },
  {
    title: "Accountability, Start to Finish",
    text: "Every project has a named project manager or crew leader who's with you from the first inspection to the final walkthrough. No hit-and-run subcontracting — Shaun started this company specifically because that wasn't good enough for homeowners.",
  },
  {
    title: "Local Knowledge, Local Commitment",
    text: "Three offices in Battle Creek, Wyoming, and Jackson means a crew that knows Michigan's freeze-thaw cycles, snow load, and storm patterns firsthand, and can respond fast when a storm hits.",
  },
  {
    title: "Standing With You Through the Insurance Process",
    text: "When storm damage is involved, we meet your adjuster on-site, handle the paperwork, and make sure permitting is done right so your claim doesn't get denied over a technicality.",
  },
  {
    title: "Family Ownership, Family Standards",
    text: "Still family-owned and operated since Shaun and Terry built it from a garage to three offices — decisions get made like it's our name on the truck, because it is.",
  },
];

const locations = [
  {
    city: "Battle Creek",
    tag: "Headquarters",
    address: "901 Capital Ave NE",
    detail: "Battle Creek, MI 49017",
    phone: "(269) 420-6623",
    page: "/roofing-battle-creek-mi/",
    lat: 42.3342,
    lng: -85.1584,
  },
  {
    city: "Wyoming",
    tag: "Grand Rapids area",
    address: "4331 Clyde Park Ave SW",
    detail: "Wyoming, MI 49509",
    phone: "(616) 367-3700",
    page: "/roofing-wyoming/",
    lat: 42.8797,
    lng: -85.68,
  },
  {
    city: "Jackson",
    address: "2600 Airport Rd, Suite 100",
    detail: "Jackson, MI 49202",
    phone: "(517) 544-0401",
    page: "/roofing-jackson-mi/",
    lat: 42.2601,
    lng: -84.459,
  },
];

const directionsUrl = (loc) =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    `${loc.address}, ${loc.detail}`
  )}`;

/*
 * About page FAQ. The first three and the fifth keep the wording already
 * published in src/components/FAQ.js; the insurance-claims, financing and
 * warranty entries are the copy supplied in Figma comment #81.
 */
const faqs = [
  [
    "Is Roach Home Improvement licensed and insured?",
    "Yes. State of Michigan license # 2101200181, Workers Compensation and Liability through Frankenmuth Insurance.",
  ],
  [
    "How long has Roach Home Improvement been in business?",
    "We have been in business for 20 years doing roof installations and many other Roofing projects.",
  ],
  [
    "Does Roach Home Improvement remove old roofing material or shingle over the existing roof?",
    "We do not recommend applying a new layer of shingles over the existing roof. At Roach we want to remove all existing roofing material to insure that the roof decking is adequate for the new roof application (No rotten wood or large gaps). Michigan code states no more than 2 layers of roofing materials plus total removal for the manufacturers warranty.",
  ],
  [
    "Does Roach Home Improvement help with insurance claims?",
    "Yes. We inspect storm and hail damage, meet with your insurance adjuster on-site, and handle the claim paperwork and permitting so the process is easier on you.",
  ],
  [
    "How long will the roofing application take on my home?",
    "No one answer to this because each home is different. An easy 1 story gable or hip roof house will be complete in 1 day. A 2 story multi slope roof with multiple layers of removal will be complete in 2 days.",
  ],
  [
    "Does Roach Home Improvement offer financing?",
    "Yes, through Michigan Saves and Synchrony, along with contractor financing programs offering 0% APR for well-qualified buyers.",
  ],
  [
    "What warranty does Roach Home Improvement provide?",
    "We offer a 25-year leak warranty along with manufacturer warranties on materials and our own workmanship guarantee, reviewed with you before work begins.",
  ],
];

const googleIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="rha-rating__icon" aria-hidden="true">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

const angiIcon = (
  <svg viewBox="0 0 18.09 23.74" xmlns="http://www.w3.org/2000/svg" className="rha-rating__icon" aria-hidden="true">
    <path
      d="m14.52,0h-5.38l-1.84,11.08s-.08,0-.12.01C3.12,11.62-.03,14.92,0,18.62c.02,1.55.56,2.75,1.54,3.74.88.89,2.09,1.38,3.31,1.38.48,0,.95-.07,1.41-.23,2.19-.73,2.92-2.5,3.26-4.13,0,0,0,0,0,0,.1-1.81-.91-3.61-2.82-4.67l-.61,3.69c-.19,1.05-.45,1.67-.99,1.86-.32.11-.72,0-.99-.28-.35-.35-.55-.74-.56-1.41-.02-1.85,1.49-3.3,3.15-3.87.52-.18,1.06-.27,1.67-.27s1.26.11,1.85.31c2.24.51,3.6,2.81,3.94,5.3l.2,1.39h3.73L14.52,0Zm-3.83,11.32l1.07-7.93h.13l1.19,8.85c-.76-.4-1.57-.71-2.4-.92Z"
      fill="#F28D24"
    />
    <path
      d="m9.52,19.39c.19-.78.62-3.75.7-4.63-.59-.19-1.24-.31-1.85-.31s-1.15.09-1.67.27c1.92,1.06,2.92,2.85,2.82,4.67Z"
      fill="#F28D24"
    />
  </svg>
);

const yelpIcon = (
  <svg version="1.1" className="rha-rating__icon" viewBox="0 0 228.097 228.097" aria-hidden="true">
    <g fill="#D31436">
      <path d="M173.22,68.06c8.204,6.784,30.709,25.392,27.042,38.455c-1.696,5.867-8.434,7.746-13.43,9.579c-11.505,4.171-23.33,7.471-35.339,9.9c-9.717,1.971-30.48,6.279-26.63-10.909c1.512-6.646,6.875-12.284,11.184-17.28c8.846-10.404,17.876-21.405,28.555-29.93c0.871-0.688,1.925-0.871,2.842-0.733C169.232,66.41,171.386,66.502,173.22,68.06z" />
      <path d="M161.119,205.197c-7.196-5.821-12.284-14.942-16.684-22.917c-4.309-7.7-11.092-17.876-12.238-26.813c-2.337-18.38,24.292-7.333,31.947-4.675c10.175,3.575,37.447,7.517,34.422,23.421c-2.521,12.971-18.151,28.784-31.213,30.801c-0.137,0.046-0.321,0-0.504,0c-0.046,0.046-0.092,0.092-0.137,0.137c-0.367,0.183-0.779,0.413-1.192,0.596C163.961,206.573,162.449,206.252,161.119,205.197z" />
      <path d="M101.58,157.896c14.484-6.004,15.813,10.175,15.721,19.984c-0.137,11.688-0.504,23.421-1.375,35.063c-0.321,4.721-0.137,10.405-4.629,13.384c-5.546,3.667-16.225,0.779-21.955-1.008c-0.183-0.092-0.367-0.183-0.55-0.229c-12.054-2.108-26.767-7.654-28.188-18.792c-0.138-1.283,0.367-2.429,1.146-3.3c0.367-0.688,0.733-1.329,1.146-1.925c1.788-2.475,3.85-4.675,5.913-6.921c3.483-5.179,7.242-10.175,11.229-14.988C85.813,172.197,92.917,161.471,101.58,157.896z" />
      <path d="M103.689,107.661c-21.13-17.371-41.71-44.276-52.344-69.164c-8.113-18.93,12.513-30.48,28.417-35.705c21.451-7.059,29.976-0.917,32.13,20.534c1.788,18.471,2.613,37.08,2.475,55.643c-0.046,7.838,2.154,20.488-2.429,27.547c0.733,2.888-3.621,4.95-6.096,2.979c-0.367-0.275-0.733-0.642-1.146-0.963C104.33,108.303,104.009,108.028,103.689,107.661z" />
      <path d="M101.397,134.566c1.696,7.517-3.621,10.542-9.854,13.384c-11.092,4.996-22.734,8.984-34.422,12.284c-6.784,1.879-17.188,6.371-23.742,1.375c-4.95-3.758-5.271-11.596-5.729-17.28c-1.008-12.696,0.917-42.993,18.517-44.276c8.617-0.596,19.388,7.104,26.447,11.138c9.396,5.409,19.48,11.596,26.492,20.076C100.159,131.862,101.03,132.916,101.397,134.566z" />
    </g>
  </svg>
);

const Stars = () => (
  <div className="rha-rating__stars" aria-hidden="true">
    {new Array(5).fill("").map((_, index) => (
      <Star fill="#f28d24" size={14} stroke="0" key={index} />
    ))}
  </div>
);

/* One inline review-site rating. Renders as a link when a profile URL exists. */
const RatingItem = ({ icon, label, href, site }) => {
  const body = (
    <>
      {icon}
      <div>
        <Stars />
        <p className="rha-rating__label">{label}</p>
      </div>
    </>
  );

  if (!href) {
    return <div className="rha-rating">{body}</div>;
  }

  return (
    <a
      className="rha-rating"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${site} rating: ${label}. Opens the ${site} profile in a new tab.`}
    >
      {body}
    </a>
  );
};

function RoachAboutPage({ page }) {
  const [lightbox, setLightbox] = useState(null);
  const [videoPlaying, setVideoPlaying] = useState(false);

  const team = page?.team || [];
  const storyBody = page?.body?.body;

  useEffect(() => {
    if (lightbox === null) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setLightbox(null);
      if (event.key === "ArrowLeft") {
        setLightbox((current) => (current - 1 + projects.length) % projects.length);
      }
      if (event.key === "ArrowRight") {
        setLightbox((current) => (current + 1) % projects.length);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [lightbox]);

  return (
    <main className="rh-homepage rha-about">
      <section className="rha-hero">
        <div className="rh-container">
          {/* Hero layout and the staggered collage keep the exact wrapper and
              column classes from the previous About page, so the photo
              arrangement stays identical to what is live today. */}
          <div className="mx-auto max-w-2xl gap-x-14 lg:mx-0 lg:flex lg:max-w-none lg:items-start">
          <div className="relative w-full max-w-xl lg:shrink-0 xl:max-w-2xl">
            <p className="rha-eyebrow">The Roach Home Improvement Story</p>
            <h1 className="rha-hero__title">Top Roofing Company in West Michigan</h1>
            <p className="rha-hero__lead">
              For over 20 years, Roach Home Improvement has delivered roofing, siding, and window
              installations to homeowners across South and West Michigan, including Battle Creek,
              Portage, Kalamazoo, Jackson, and the surrounding communities. As a GAF Master Elite and
              Owens Corning certified contractor, we&rsquo;ve completed 5,000+ roof replacements, and
              we back every job with a 25-year leak warranty and our own workmanship guarantee.
            </p>

            <div className="rha-ratings">
              <RatingItem icon={googleIcon} label="5/5 Rating" href={GOOGLE_REVIEWS} site="Google" />
              <RatingItem icon={angiIcon} label="5/5 Rating" site="Angi" />
              <RatingItem icon={yelpIcon} label="4/5 Rating" href={YELP_PROFILE} site="Yelp" />
            </div>

            {/* Figma comment #80: this button did nothing on the live page. */}
            <Link className="rh-button rh-button--red rha-hero__cta" to="/contact/">
              Get a FREE Estimate <ArrowRight size={16} aria-hidden="true" />
            </Link>

            <div className="rha-hero__badges">
              <a href={BBB_PROFILE} target="_blank" rel="noopener noreferrer">
                <img
                  src={asset("bbb-a-plus-rating.webp")}
                  alt="BBB A+ rated accredited business — view the Roach Home Improvement BBB profile"
                  loading="lazy"
                />
              </a>
              <a href={GAF_PROFILE} target="_blank" rel="noopener noreferrer">
                <img
                  src={asset("gaf-master-elite-contractor.webp")}
                  alt="GAF Master Elite certified contractor — view the Roach Home Improvement GAF profile"
                  loading="lazy"
                />
              </a>
            </div>

            {/* The design keeps the three figures inside the hero's left
                column, alongside the photo collage. */}
            <dl className="rha-stats">
              {stats.map(([value, label]) => (
                <div key={label}>
                  <dt>{value}</dt>
                  <dd>{label}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="rha-hero__collage mt-14 flex justify-end gap-8 sm:-mt-44 sm:justify-start sm:pl-20 lg:mt-0 lg:pl-0">
            <div className="ml-auto w-44 flex-none space-y-8 pt-32 sm:ml-0 sm:pt-80 lg:order-last lg:pt-36 xl:order-none xl:pt-80">
              {heroCollage[0].map((src) => (
                <div className="relative" key={src}>
                  <img
                    src={src}
                    alt=""
                    className="aspect-[2/3] w-full rounded-xl bg-gray-900/5 object-cover shadow-lg"
                  />
                  <div className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-gray-900/10" />
                </div>
              ))}
            </div>
            <div className="mr-auto w-44 flex-none space-y-8 sm:mr-0 sm:pt-52 lg:pt-36">
              {heroCollage[1].map((src) => (
                <div className="relative" key={src}>
                  <img
                    src={src}
                    alt=""
                    className="aspect-[2/3] w-full rounded-xl bg-gray-900/5 object-cover shadow-lg"
                  />
                  <div className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-gray-900/10" />
                </div>
              ))}
            </div>
            <div className="w-44 flex-none space-y-8 pt-32 sm:pt-0">
              {heroCollage[2].map((src) => (
                <div className="relative" key={src}>
                  <img
                    src={src}
                    alt=""
                    className="aspect-[2/3] w-full rounded-xl bg-gray-900/5 object-cover shadow-lg"
                  />
                  <div className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-gray-900/10" />
                </div>
              ))}
            </div>
          </div>
          </div>
        </div>
      </section>

      <section className="rha-story">
        <div className="rh-container">
          <div className="rha-story__head">
            <p className="rha-eyebrow">Our Story</p>
            <h2 className="rha-story__title">20+ Years of Serving South-West Michigan</h2>
          </div>

          <div className="rha-story__grid">
            {storyBody ? (
              <div className="rha-story__copy" dangerouslySetInnerHTML={{ __html: storyBody }} />
            ) : (
              <div className="rha-story__copy" />
            )}

            <div>
              {/* Figma comment #79 — same click-to-load video as the homepage. */}
              <div className="rha-story__video">
                {videoPlaying ? (
                  <iframe
                    className="rha-story__video-frame"
                    src="https://www.youtube.com/embed/NZA-mxnVO0E?autoplay=1&rel=0&modestbranding=1"
                    title="Shaun Roach introduces Roach Home Improvement"
                    loading="lazy"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                ) : (
                  <button
                    type="button"
                    className="rha-story__video-trigger"
                    onClick={() => setVideoPlaying(true)}
                    aria-label="Play video: Shaun Roach introduces Roach Home Improvement"
                  >
                    <img
                      src={asset("shaun-roach-video-poster-1032.webp")}
                      alt="Shaun Roach beside a Roach Home Improvement truck"
                      loading="lazy"
                      width="1032"
                      height="526"
                    />
                    <span className="rha-story__play" aria-hidden="true">
                      ▶
                    </span>
                  </button>
                )}
              </div>

              <dl className="rha-spec">
                {companyFacts.map(([label, value, href]) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>{href ? <a href={href}>{value}</a> : value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </section>

      <section className="rh-accreditations" aria-label="Accreditations and certifications">
        <div className="rh-container">
          <img
            src="/assets/logos/products/CHAMBERS-OF-COMMERCE.webp"
            alt="Battle Creek Area Chamber accredited"
          />
          {/* The design uses the green "Lead-Safe Certified Firm" badge (epa.svg),
              not the circular agency seal the homepage strip uses. */}
          <img src="/assets/logos/products/epa-min.svg" alt="EPA Lead-Safe certified firm" />
          <img
            src="/assets/logos/products/MS-AuthorizedContractor-Badge-500px.webp"
            alt="Michigan Saves authorized contractor"
          />
          <img
            src="/assets/logos/products/hba-michigan-min.svg"
            alt="Home Builders Association of Michigan"
          />
          <a href={BBB_PROFILE} target="_blank" rel="noopener noreferrer">
            <img src="/assets/logos/products/bbb.svg" alt="BBB accredited business" />
          </a>
        </div>
      </section>

      <section className="rha-mission" aria-label="Our mission and vision">
        <div className="rh-container rha-split">
          {pillars.map((pillar) => (
            <article className={pillar.reversed ? "is-reversed" : ""} key={pillar.title}>
              <div className="rha-split__copy">
                <h3>{pillar.title}</h3>
                <p>{pillar.text}</p>
              </div>
              <img src={pillar.image} alt={pillar.alt} loading="lazy" />
            </article>
          ))}
        </div>
      </section>

      <section className="rh-projects" id="projects">
        <div className="rh-container">
          <h2 className="rh-section-title">Check Out Our Work Across the Michigan</h2>
          <p className="rh-section-lead">
            At Roach Home Improvement, we pride ourselves on delivering top-quality roofing solutions
            to homeowners
          </p>
          <div className="rh-project-grid">
            {projects.map((project, index) => (
              <button
                type="button"
                key={project.src}
                onClick={() => setLightbox(index)}
                aria-label={`Enlarge project ${index + 1}`}
              >
                <img src={project.thumb} alt={project.alt} loading="lazy" width="640" height="480" />
              </button>
            ))}
          </div>
          <p className="rh-tap-note">Tap images to enlarge</p>
        </div>
      </section>

      <section className="rh-testimonials" id="reviews">
        <div className="rh-container">
          <h2 className="rh-section-title">Read What Our Clients Say About Us</h2>
          <div className="rh-testimonials__grid">
            <div className="rh-review-summary">
              <strong>
                4.9<span>/5</span>
              </strong>
              <div className="rh-stars" aria-label="Five stars">
                ★★★★★
              </div>
              <h3>
                Hundreds of <em>Happy</em> Customers
              </h3>
              <p>
                Real reviews from real customers — see what they have to say about their experience
                with us.
              </p>
              <Link className="rh-button rh-button--red rh-review-summary__cta" to="/contact/">
                Request a FREE Estimate Now
                <ArrowRight size={16} aria-hidden="true" />
              </Link>
            </div>
            {testimonials.map((testimonial) => (
              <article key={testimonial.author}>
                <h3>{testimonial.title}</h3>
                <div className="rh-stars" aria-label="Five stars">
                  ★★★★★
                </div>
                <p>{testimonial.body}</p>
                <footer>
                  <span aria-hidden="true" /> {testimonial.author}
                </footer>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="rha-team">
        <div className="rh-container">
          <div className="rha-team__head">
            <h2>Meet Our Team</h2>
            <p>We will make your home&rsquo;s upgrade a smooth process from start to finish.</p>
          </div>
          <ul className="rha-team__grid" role="list">
            {team.map((person) => {
              const image = getImage(person.gatsbyImageData);
              return (
                <li key={person.id || person.title}>
                  <div className="rha-team__photo">
                    {image ? <GatsbyImage image={image} alt={person.title || ""} /> : null}
                  </div>
                  <h3>{person.title}</h3>
                  {person.description ? <p>{person.description}</p> : null}
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      <section className="rha-values">
        <div className="rh-container">
          <h2 className="rh-section-title">Core Values of Roach Home Improvement</h2>
          <div className="rha-values__grid">
            {coreValues.map((value, index) => (
              <article key={value.title}>
                <span>Value {index + 1}</span>
                <h3>{value.title}</h3>
                <p>{value.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="rh-locations" id="locations">
        <div className="rh-container">
          <div className="rh-locations__head">
            <h2>Our 3 Headquarter Locations in West Michigan</h2>
            <p>
              3 locations in different cities gives us a privilege to provide emergency roofing
              services across the West and South Michigan.
            </p>
          </div>

          <LocationsMap locations={locations} />

          <div className="rh-locations__cards">
            {locations.map((location) => (
              <article className="rh-location-card" key={location.city}>
                <h3>
                  {location.city}
                  {location.tag && <span>{location.tag}</span>}
                </h3>
                <address>
                  {location.address}
                  <br />
                  {location.detail}
                </address>
                {location.phone && (
                  <a
                    className="rh-location-card__phone"
                    href={`tel:${siteConfig.phoneE164(location.phone)}`}
                  >
                    <Phone size={15} aria-hidden="true" /> {location.phone}
                  </a>
                )}
                <div className="rh-location-card__links">
                  <a
                    className="rh-location-card__directions"
                    href={directionsUrl(location)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Get directions <ArrowRight size={15} aria-hidden="true" />
                  </a>
                  <Link className="rh-location-card__page" to={location.page}>
                    View {location.city} roofing
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="rh-faq">
        <div className="rh-container">
          <h2 className="rh-section-title">Frequently Asked Questions</h2>
          <p className="rh-section-lead">
            Home owners have questions, Roach Home Improvement provides answers.
          </p>
          <div className="rh-faq__list">
            {faqs.map(([question, answer]) => (
              <details key={question}>
                <summary>
                  {question}
                  <span aria-hidden="true">−</span>
                </summary>
                <p>{answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="rh-contact" id="estimate">
        <div className="rh-container rh-contact__grid">
          <article className="rh-contact-card">
            <h2>Get In Touch With Us</h2>
            <a href="mailto:office@roachhomeimprovement.com">
              <Mail /> office@roachhomeimprovement.com
            </a>
            <a href="tel:+12694206623">
              <Phone /> (269) 420-6623
            </a>
            <div className="rh-socials" aria-label="Social media">
              <span aria-label="Instagram">◎</span>
              <span aria-label="Facebook">f</span>
              <span aria-label="TikTok">♪</span>
              <span aria-label="X">X</span>
              <a
                href="https://www.youtube.com/watch?v=NZA-mxnVO0E"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
              >
                ▶
              </a>
            </div>
          </article>
          <article className="rh-contact-form-card">
            <span className="rh-special-label">Online Special</span>
            <h2>Request a Free Roofing Estimate With Our Experts!</h2>
            <p>Request Free Pricing Today for Your Remodeling Needs!</p>
            <ContactForm hideLabels showPlaceholders grayInputBg />
          </article>
        </div>
      </section>

      <div className="rh-prefooter-band" aria-hidden="true" />

      {lightbox !== null ? (
        <div
          className="rh-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Project gallery"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setLightbox(null);
          }}
        >
          <div className="rh-lightbox__dialog">
            <button
              className="rh-lightbox__close"
              type="button"
              onClick={() => setLightbox(null)}
              aria-label="Close project gallery"
              autoFocus
            >
              <X aria-hidden="true" />
            </button>
            <button
              className="rh-lightbox__previous"
              type="button"
              onClick={() => setLightbox((lightbox - 1 + projects.length) % projects.length)}
              aria-label="View previous project"
            >
              <ChevronLeft aria-hidden="true" />
            </button>
            <figure>
              <img src={projects[lightbox].src} alt={projects[lightbox].alt} />
              <figcaption>
                {lightbox + 1} / {projects.length}
              </figcaption>
            </figure>
            <button
              className="rh-lightbox__next"
              type="button"
              onClick={() => setLightbox((lightbox + 1) % projects.length)}
              aria-label="View next project"
            >
              <ChevronRight aria-hidden="true" />
            </button>
          </div>
        </div>
      ) : null}
    </main>
  );
}

export default RoachAboutPage;
